import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ENDPOINTS,
  type AnalyticsContractsListResponse,
  type CloudeqEntityKey,
  type ContractSummary,
  type LifecycleStatus,
  type ProcessingStatus,
  type Region,
} from '@clm/shared-types';

import { fetchAnalyticsContractsList } from '../../api/analytics';
import { ContractTypeTag } from '../../components/ContractTypeTag';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { TRUST_LABELS, TRUST_TONES, processingLabel } from '../../components/trust-labels';
import { ANALYTICS_FORBIDDEN_COPY } from '../analytics-shared';
import { PanelProblem } from '../quality/panel';
import { toProblem, type Panel } from '../quality/panel-state';

/**
 * The list behind a KPI or status-mix tile — CLM-95 (D-95-1, D-95-3).
 *
 * `RenewalsDueList`'s sibling: same fetch/page/error shape, over
 * `GET /v1/analytics/contracts-list` instead of `/renewals-due`, and over
 * plain `ContractSummary` rows instead of the richer renewal row (no
 * notification tiers, no due-date verification — this list has no date
 * ladder to show). `AnalyticsDashboard.tsx` owns whether this mounts at all
 * (the shared `?panel=` state, D-95-3) and which single filter it renders —
 * this component never reads the URL itself.
 */

const ENDPOINT_PATH = ENDPOINTS['analytics-contracts-list'].path;
const PAGE_LIMIT = 20;

export interface ContractsListFilters {
  readonly lifecycleStatus?: LifecycleStatus[];
  readonly processingStatus?: ProcessingStatus[];
  readonly expiringWithinDays?: number;
}

function filterKeyOf(filters: ContractsListFilters): string {
  return [
    filters.lifecycleStatus?.join(',') ?? '',
    filters.processingStatus?.join(',') ?? '',
    filters.expiringWithinDays ?? '',
  ].join('|');
}

const COLUMNS: readonly DataTableColumn<ContractSummary>[] = [
  {
    id: 'contract',
    header: 'Contract',
    cell: (row) => <Link to={`/contracts/record/${encodeURIComponent(row.contract_key)}`}>{row.contract_key}</Link>,
  },
  { id: 'client', header: 'Client', cell: (row) => row.primary_party_display_name },
  /* CLM-110 (R-110-23) — provisional until an administrator confirms it. */
  { id: 'type', header: 'Type', cell: (row) => <ContractTypeTag type={row.contract_type} confirmation={row.contract_type_confirmation} /> },
  { id: 'lifecycle', header: 'Lifecycle', cell: (row) => row.lifecycle_status },
  { id: 'processing', header: 'Processing', cell: (row) => processingLabel(row.processing_status) },
  { id: 'expiry', header: 'Expiry', cell: (row) => row.expiry_date ?? '—' },
  { id: 'owner', header: 'Owner', cell: (row) => row.contract_owner_upn ?? '—' },
  /*
   * CLM-108 (R-108-14, R-108-15) — the trust axis, read from `data_trust` and
   * nothing else. This column used to be headed "Verified" and to print it
   * whenever `has_unverified_fields` was false, which is also true of a
   * contract with no field row at all (not yet extracted, or held) and of one
   * whose fields were auto-approved by policy. Untagged only for a contract
   * every field of which a person settled.
   */
  {
    id: 'trust',
    header: 'Trust',
    cell: (row) =>
      row.data_trust === 'Verified' ? (
        TRUST_LABELS[row.data_trust]
      ) : (
        <span className={`tag ${TRUST_TONES[row.data_trust]}`}>{TRUST_LABELS[row.data_trust]}</span>
      ),
  },
];

type ListState = Panel<AnalyticsContractsListResponse>;

export function ContractsListPanel({
  title,
  emptyCopy,
  partyKey,
  region,
  entityKey,
  filters,
  onCollapse,
}: {
  readonly title: string;
  readonly emptyCopy: string;
  readonly partyKey: string | null;
  readonly region: Region | null;
  readonly entityKey: CloudeqEntityKey | null;
  readonly filters: ContractsListFilters;
  readonly onCollapse: () => void;
}) {
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [items, setItems] = useState<readonly ContractSummary[]>([]);
  const filterKey = `${partyKey ?? ''}|${region ?? ''}|${entityKey ?? ''}|${filterKeyOf(filters)}`;
  const [page, setPage] = useState<{ readonly cursor: string; readonly forFilters: string } | null>(null);
  const cursor = page !== null && page.forFilters === filterKey ? page.cursor : null;
  const [nonce, setNonce] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    if (cursor === null) {
      setItems([]);
      setState({ kind: 'loading' });
    } else {
      setLoadingMore(true);
    }

    void (async () => {
      try {
        const response = await fetchAnalyticsContractsList(
          {
            ...(partyKey === null ? {} : { party_key: partyKey }),
            ...(region === null ? {} : { region }),
            ...(entityKey === null ? {} : { cloudeq_entity: entityKey }),
            ...(filters.lifecycleStatus === undefined ? {} : { lifecycle_status: filters.lifecycleStatus }),
            ...(filters.processingStatus === undefined ? {} : { processing_status: filters.processingStatus }),
            ...(filters.expiringWithinDays === undefined ? {} : { expiring_within_days: filters.expiringWithinDays }),
            page: { limit: PAGE_LIMIT, ...(cursor === null ? {} : { cursor }) },
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setItems((previous) => (cursor === null ? response.items : [...previous, ...response.items]));
        setState({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState(toProblem(error));
      } finally {
        if (!controller.signal.aborted) setLoadingMore(false);
      }
    })();

    return () => {
      controller.abort();
    };
    // filterKey stands in for filters/partyKey/region/entityKey — see RenewalsDueList's identical note.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, cursor, nonce]);

  const reload = () => {
    setPage(null);
    setNonce((n) => n + 1);
  };

  const nextCursor = state.kind === 'ready' ? state.response.page.next_cursor : null;
  /* Three call sites can mount this component (kpi-active/kpi-expiring-30/mix), never
   * simultaneously (one shared activePanel, D-95-3) — but a stable per-instance id via
   * useId() rather than a literal is the repo's own convention for this exact situation
   * (ClauseDetail.tsx, ClauseLibrary.tsx, AliasesPanel.tsx), so it costs nothing to follow. */
  const headingId = useId();

  return (
    <section className="card analytics-section contracts-list" aria-labelledby={headingId}>
      <div className="analytics-head">
        {/* h4, not h3 (D-95-3 follow-up): the tile/legend-chip trigger that opens this
            panel is itself wrapped in an <h3> (StatTile, Donut's legend button), so an
            identical <h3>{title}</h3> here would duplicate that landmark's name and
            level — two consecutive "Active, heading level 3" stops for a screen-reader
            user navigating by heading. h4 nests this panel correctly under its trigger. */}
        <h4 id={headingId}>{title}</h4>
        {state.kind === 'ready' ? (
          <p className="mut">{String(state.response.page.total_in_scope ?? items.length)} in scope</p>
        ) : null}
        <button type="button" className="btn sm" onClick={onCollapse}>
          Collapse
        </button>
      </div>

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {title}…
        </p>
      ) : null}

      {state.kind === 'problem' ? (
        <PanelProblem panel={title} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} forbiddenCopy={ANALYTICS_FORBIDDEN_COPY} />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <DataTable caption={title} columns={COLUMNS} rows={items} rowKey={(row) => row.contract_key} busy={loadingMore} empty={<p className="mut">{emptyCopy}</p>} />
          {nextCursor === null ? null : (
            <button
              type="button"
              className="btn"
              disabled={loadingMore}
              onClick={() => {
                setPage({ cursor: nextCursor, forFilters: filterKey });
              }}
            >
              Load more
            </button>
          )}
        </>
      ) : null}
    </section>
  );
}
